import { cn } from "@/lib/utils";
import React, { useEffect, useRef } from "react";

type ScrollableProps = {
  children: React.ReactNode;
  maxShadowHeight?: number;
  outerClassName?: string;
  innerClassName?: string;
  fromColorClassName?: `from-${string}`;
};

const DEFAULT_MAX_SHADOW_HEIGHT = 50;

export const Scrollable: React.FC<ScrollableProps> = ({
  outerClassName,
  innerClassName,
  maxShadowHeight = DEFAULT_MAX_SHADOW_HEIGHT,
  // fromColorClassName = "from-background",
  children,
}) => {
  const scrollableDivRef = useRef<HTMLDivElement>(null);
  const topGradientOverlayRef = useRef<HTMLDivElement>(null);
  const bottomGradientOverlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollableDiv = scrollableDivRef.current;
    const topGradientOverlay = topGradientOverlayRef.current;
    const bottomGradientOverlay = bottomGradientOverlayRef.current;

    if (!scrollableDiv || !topGradientOverlay || !bottomGradientOverlay) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollableDiv;

      // Top gradient
      topGradientOverlay.style.height = `${Math.min(
        maxShadowHeight,
        scrollTop,
      )}px`;

      // Bottom gradient
      bottomGradientOverlay.style.height = `${Math.min(
        maxShadowHeight,
        scrollHeight - clientHeight - scrollTop,
      )}px`;
    };

    scrollableDiv.addEventListener("scroll", handleScroll);

    // Observe size changes of the scrollable container itself
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(scrollableDiv);

    // Observe DOM changes in children (content updates)
    const mutationObserver = new MutationObserver(handleScroll);
    mutationObserver.observe(scrollableDiv, {
      childList: true,
      subtree: true,
    });

    handleScroll();

    return () => {
      scrollableDiv.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [scrollableDivRef, topGradientOverlayRef, bottomGradientOverlayRef]);

  return (
    <div
      data-slot="scrollable-outer"
      className={cn("relative overflow-hidden", outerClassName)}
    >
      <div
        data-slot="scrollable-inner"
        className={cn(
          "relative my-4 h-full w-full overflow-x-auto overflow-y-auto",
          innerClassName,
        )}
        ref={scrollableDivRef}
      >
        {children}
      </div>
      {/* @TEMP remove until reimplemented */}
      {/* <div
        data-slot="scrollable-top-gradient"
        className={cn(
          "pointer-events-none absolute top-0 left-0 h-0 w-full bg-gradient-to-b to-transparent",
          fromColorClassName ?? "from-background",
        )}
        ref={topGradientOverlayRef}
      />
      <div
        data-slot="scrollable-bottom-gradient"
        className={cn(
          "pointer-events-none absolute bottom-0 left-0 h-0 w-full bg-gradient-to-t to-transparent",
          fromColorClassName ?? "from-background",
        )}
        ref={bottomGradientOverlayRef}
      /> */}
    </div>
  );
};
